import { AsyncPipe } from '@angular/common';
import {
	Component,
	EventEmitter,
	Output,
	Input,
	OnInit,
	AfterViewInit,
	ChangeDetectionStrategy,
	ViewChildren,
	QueryList,
	ElementRef,
	Injector,
	inject,
	DestroyRef,
	Optional,
	effect,
	Inject
} from '@angular/core';
import { DragDropModule, CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Observable, Subject, map, combineLatest, startWith, BehaviorSubject, filter } from 'rxjs';
import { Activity } from '@go-modules/interfaces/activity';
import { Task, TaskType } from '@go-modules/interfaces/task';
import { NgxTaskService } from '@go-modules/services/task/task.service';
import { SelectedService, selectedServiceToken } from 'go-modules/services/selected/selected.service';
import {
	AccessibleDropListDirective,
	type AccessibleDropListItemId
} from '../../directives/accessible-drop-list/accessible-drop-list.directive';
import { ConfirmDialogComponent, ConfirmDialogData } from '../dialogs/confirm-dialog/confirm-dialog.component';
import { NgxActivityService } from '@go-modules/services/activity/activity.service';
import { TrackByPropPipeModule } from '@go-modules/pipes/track-by-prop/track-by-prop-pipe.module';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ROUTE_NAMES, RouterService } from '@go-modules/services/router/router.service';
import { EventService, GoEvent } from '@go-modules/services/event/event.service';
import { EVENT_NAMES } from '@go-modules/services/event/event-names.constants';
import { ActivityEditModeService } from '@go-modules/services/activity-edit-mode/activity-edit-mode.service';
import { ActiveBrandService } from '@go-modules/services/active-brand';
import { NavigationEnd, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
	ACTIVITY_EDITOR_CONTEXT,
	ActivityEditorContext
} from '@go-modules/components/dialogs/activity-preview-editor/activity-editor-context';

@Component({
	selector: 'activity-task-pane',
	imports: [
		DragDropModule,
		TranslateModule,
		AccessibleDropListDirective,
		TrackByPropPipeModule,
		MatIconModule,
		MatTooltipModule,
		AsyncPipe
	],
	templateUrl: './activity-task-pane.component.html',
	styleUrls: ['./activity-task-pane.component.scss'],
	changeDetection: ChangeDetectionStrategy.Default
})
export class ActivityTaskPaneComponent implements OnInit, AfterViewInit {
	@Input() public isEditMode = false;
	@Input() public inMobilePanel = false;
	@Input() public titleKey = 'activity-task-pane-title';
	@Output() public taskSelected = new EventEmitter<void | Task>();
	@ViewChildren('listItem') public listItems: QueryList<ElementRef>;
	public selectedTaskType: TaskType | null = null;
	public activeItem: number;
	public originalIndex: { [key: string]: number } = {};
	public filteredTasks$ = new BehaviorSubject<Task[]>([]);
	public visibleFilteredTasks$: Observable<Task[]>;
	public visibleModifiedTasks$: Observable<Task[]>;
	public recommendedTasks$: Observable<Task[]>;
	public firstIncompleteTaskType: TaskType | null = null;
	public modifiedTasks: Task[] = [];
	public isEditorContextMode = false;
	private modifiedTasks$ = new BehaviorSubject<Task[]>([]);
	private deletedTasks: Map<TaskType, Task> = new Map();
	private refreshRecommended$ = new Subject<void>();
	private excludedTypes$ = new BehaviorSubject<Set<TaskType>>(new Set());
	private isKeyboardReorderActive = false;
	private defaultActivityPrivacy: Pick<Activity, 'peer_review' | 'public_feedback'> | null = null;
	readonly #destroyRef = inject(DestroyRef);

	constructor (
		public activityService: NgxActivityService,
		private taskService: NgxTaskService,
		private dialog: MatDialog,
		private translate: TranslateService,
		private routerService: RouterService,
		private eventService: EventService,
		private activityEditModeService: ActivityEditModeService,
		private activeBrandService: ActiveBrandService,
		private injector: Injector,
		@Optional() @Inject(ACTIVITY_EDITOR_CONTEXT) private editorContext: ActivityEditorContext | null,
		@Inject(selectedServiceToken) private selectedService: SelectedService
	) { }

	public get translationParams () {
		return this.activeBrandService.getTranslationParams();
	}

	public getTaskReorderId (task: Task): AccessibleDropListItemId {
		return task.id === 0 ? task.type : task.id;
	}

	public focusSelectedTask (): void {
		const visibleTasks = this.getVisibleTasksForFocus();
		if (!visibleTasks.length) {
			return;
		}

		const selectedIndex = visibleTasks.findIndex((task) => task.type === this.selectedTaskType);
		this.activeItem = selectedIndex >= 0 ? selectedIndex : 0;
		this.setFocus();
	}

	public ngOnInit (): void {
		this.isEditorContextMode = this.editorContext != null;
		this.loadDefaultActivityPrivacy();

		this.visibleFilteredTasks$ = combineLatest([this.filteredTasks$, this.excludedTypes$]).pipe(
			map(([tasks, excluded]) => tasks.filter((t) => !excluded.has(t.type)))
		);

		this.visibleModifiedTasks$ = combineLatest([this.modifiedTasks$, this.excludedTypes$]).pipe(
			map(([tasks, excluded]) => tasks.filter((t) => !excluded.has(t.type)))
		);

		if (this.editorContext != null) {
			this.initializeEditorContextMode();
			return;
		}

		const activity = this.selectedService.getActivity();
		const currentParams = this.routerService.getCurrentParams();
		if (currentParams.type) {
			this.selectedTaskType = currentParams.type;
		}

		const router = this.injector.get(Router);
		router.events.pipe(
			filter((event) => event instanceof NavigationEnd),
			takeUntilDestroyed(this.#destroyRef)
		).subscribe(() => this.selectedTaskType = this.routerService.getCurrentParams().type);

		// Restore modifiedTasks if we're re-initializing with existing unsaved changes
		// (e.g., after a canceled navigation that destroyed/recreated the component)
		if (this.isEditMode && this.activityEditModeService.hasUnsavedChanges()) {
			const editedTasks = this.activityEditModeService.currentTasks();
			if (editedTasks && editedTasks.length > 0) {
				this.modifiedTasks = [...editedTasks];
				this.modifiedTasks$.next([...editedTasks]);
			}
		}

		this.taskService.getTasks(activity.activity_id).pipe(
			takeUntilDestroyed(this.#destroyRef)
		).subscribe((tasks) => {
			this.processAndUpdateTasks(tasks, activity);
		});

		this.activityEditModeService.changesReverted$.pipe(
			takeUntilDestroyed(this.#destroyRef)
		).subscribe(() => this.resetModifiedTasks());

		this.recommendedTasks$ = this.getRecommendedTasks();

		this.updateOriginalIndex();

		this.eventService.events
			.pipe(
				filter(
					(ev: GoEvent) =>
						[
							EVENT_NAMES.ACTIVITY_EDIT_ACTIVITY_TYPE_CHANGE,
							EVENT_NAMES.ACTIVITY_EDIT_CANCEL_CONFIRMED
						].includes(ev.name as EVENT_NAMES)
				),
				takeUntilDestroyed(this.#destroyRef)
			)
			.subscribe((ev: GoEvent) => {
				const excluded = this.getExcludedTypesForActivity(ev);
				this.excludedTypes$.next(excluded);
			});
	}

	private getExcludedTypesForActivity (ev: GoEvent): Set<TaskType> {
		if (ev.name === EVENT_NAMES.ACTIVITY_EDIT_CANCEL_CONFIRMED) {
			return new Set();
		}

		if (!ev.data.has_response_media) {
			return new Set([TaskType.Reflect, TaskType.Submit]);
		}

		return new Set();
	}

	public ngAfterViewInit (): void {
		if (!this.inMobilePanel) {
			this.taskSelected.emit();
		}

		this.listItems.changes.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => {
			this.setFocus();
		});
	}

	public setFocus () {
		if (this.isEditMode) {
			const dragHandle = this.listItems.toArray()[this.activeItem]?.nativeElement.querySelector('.drag-handle');
			dragHandle?.focus();
		} else {
			this.listItems.toArray()[this.activeItem]?.nativeElement.focus();
		}
	}

	public drop (event: CdkDragDrop<Task[]>) {
		if (event.previousContainer !== event.container) {
			if (event.container.id === 'task-list' && event.previousContainer.id === 'recommended-list') {
				this.onAddRecommendedTask(event.item.data, event.currentIndex);
			}
			else if (event.container.id === 'recommended-list' && event.previousContainer.id === 'task-list') {
				this.onTaskRemove(event.item.data);
			}
		} else {
			if (event.container.id === 'task-list') {
				moveItemInArray(this.modifiedTasks, event.previousIndex, event.currentIndex);
				this.activeItem = event.currentIndex;
				this.setFocus();
				this.updateOriginalIndex();
				this.updateTaskSortOrders();
				this.updateEditedTasks(this.modifiedTasks);
			}
		}
	}

	public onActiveItemChange ({ currentIndex, newIndex }) {
		this.isKeyboardReorderActive = true;
		moveItemInArray(this.modifiedTasks, currentIndex, newIndex);
		this.activeItem = newIndex;
		this.setFocus();
		this.updateTaskSortOrders();
		this.updateEditedTasks(this.modifiedTasks);
	}

	public onFinalizePosition () {
		this.isKeyboardReorderActive = false;
		this.updateOriginalIndex();
	}

	public updateOriginalIndex () {
		this.modifiedTasks.forEach((task, index) => {
			this.originalIndex[this.getTaskReorderId(task)] = index;
		});
	}

	public onCancelReorder ({ itemId }) {
		const originalIndex = this.originalIndex[itemId];
		// Find the actual current index in modifiedTasks (not the filtered visible array)
		const actualCurrentIndex = this.modifiedTasks.findIndex((task) => this.getTaskReorderId(task) === itemId);
		if (actualCurrentIndex !== -1 && originalIndex !== undefined) {
			moveItemInArray(this.modifiedTasks, actualCurrentIndex, originalIndex);
			this.updateTaskSortOrders();
			this.updateEditedTasks(this.modifiedTasks);
			const excludedTypes = this.excludedTypes$.value;
			const visibleTasks = this.modifiedTasks.filter((task) => !excludedTypes.has(task.type));
			const visibleIndex = visibleTasks.findIndex((task) => this.getTaskReorderId(task) === itemId);
			if (visibleIndex !== -1) {
				this.activeItem = visibleIndex;
			}
			this.setFocus();
		}
		this.isKeyboardReorderActive = false;
		this.updateOriginalIndex();
	}

	public onTaskClick (task: Task, event?: MouseEvent): void {
		if (event != null && this.isNestedTaskControl(event.target)) {
			return;
		}

		this.taskSelected.emit(task);

		if (this.editorContext != null) {
			this.editorContext.selectTask(task.type);
			this.selectedTaskType = this.editorContext.selectedTaskType();
			return;
		}

		const activity = this.getCurrentActivity();
		if (activity) {
			this.selectedTaskType = task.type;

			const currentQueryParams = this.routerService.getCurrentQueryParams();
			this.routerService.navigateTo(ROUTE_NAMES.DASHBOARD_ACTIVITY_TASK, {
				activity_id: activity.activity_id,
				type: task.type
			}, {
				queryParams: currentQueryParams
			});
		}
	}

	public onTaskKeydown (event: KeyboardEvent): void {
		if (this.isNestedTaskControl(event.target)) {
			return;
		}

		event.preventDefault();
	}

	public onTaskKeyup (event: KeyboardEvent, task: Task): void {
		if (this.isNestedTaskControl(event.target)) {
			return;
		}

		this.onTaskClick(task);
	}

	public shouldShowLearnerTourPulse (task: Task): boolean {
		return !this.isEditMode &&
			!this.inMobilePanel &&
			!task.completed &&
			this.firstIncompleteTaskType === task.type &&
			this.selectedTaskType !== this.firstIncompleteTaskType;
	}

	public onTaskRemove (task: Task): void {
		if (task.type === TaskType.Present) {
			return;
		}

		if (this.selectedTaskType === task.type) {
			this.taskSelected.emit();
		}

		const dialogRef: MatDialogRef<ConfirmDialogComponent, boolean> = ConfirmDialogComponent.open(this.dialog, {
			data: {
				title: this.translate.instant('activity-task-list_remove-task-title'),
				message: this.translate.instant('activity-task-list_remove-task-message'),
				confirmText: this.translate.instant('common_remove'),
				cancelText: this.translate.instant('common_cancel')
			} as ConfirmDialogData
		});

		dialogRef.afterClosed()
			.pipe(takeUntilDestroyed(this.#destroyRef))
			.subscribe((confirm) => {
				if (confirm) {
					const index = this.modifiedTasks.findIndex((t) => this.taskMatches(t, task));
					if (index !== -1) {
						this.deletedTasks.set(task.type, task);
						this.modifiedTasks.splice(index, 1);
						this.updateTaskSortOrders();
						this.updateEditedTasks(this.modifiedTasks);
						this.refreshRecommended$.next();

						// If we removed the currently selected task, navigate to another one
						if (this.selectedTaskType === task.type) {
							const activity = this.getCurrentActivity();
							const targetTask = this.modifiedTasks[0];
							if (targetTask == null) {
								this.selectedTaskType = null;
								this.taskSelected.emit();
								return;
							}
							this.selectedTaskType = targetTask.type;
							this.taskSelected.emit(targetTask);
							if (this.editorContext != null) {
								this.editorContext.selectTask(targetTask.type);
								return;
							}
							if (activity == null) {
								return;
							}
							const currentQueryParams = this.routerService.getCurrentQueryParams();
							this.routerService.navigateTo(ROUTE_NAMES.DASHBOARD_ACTIVITY_TASK, {
								activity_id: activity.activity_id,
								type: targetTask.type
							}, {
								queryParams: currentQueryParams
							});
						}
					}
				}
			});
	}

	public onAddRecommendedTask (task: Task, insertAtIndex?: number): void {
		let newTask: Task;
		const activity = this.getCurrentActivity();
		if (activity == null) {
			return;
		}

		const deletedTask = this.deletedTasks.get(task.type);
		if (deletedTask) {
			newTask = {
				...deletedTask,
				deleted_at: null,
				deleted_by: null
			};
			this.deletedTasks.delete(task.type);
		} else {
			const minId = Math.min(...this.modifiedTasks.map((t) => t.id), 0);
			newTask = {
				id: minId - 1,
				type: task.type,
				completed: false,
				name: this.activityService.getTaskTitleAndDescription(activity, task).title,
				requirements: this.taskService.getTaskTypeDefaultRequirement(task.type),
				sort_order: 0
			};
		}

		if (insertAtIndex !== undefined && insertAtIndex >= 0 && insertAtIndex <= this.modifiedTasks.length) {
			this.modifiedTasks.splice(insertAtIndex, 0, newTask);
		} else {
			this.modifiedTasks.push(newTask);
		}

		this.updateTaskSortOrders();
		this.updateEditedTasks(this.modifiedTasks);
		this.applyDefaultPrivacyForReactTask(newTask);
		this.refreshRecommended$.next();

		// Navigate to the newly added task
		this.selectedTaskType = newTask.type;
		this.taskSelected.emit(newTask);
		if (this.editorContext != null) {
			this.editorContext.selectTask(newTask.type);
			return;
		}
		const currentQueryParams = this.routerService.getCurrentQueryParams();
		this.routerService.navigateTo(ROUTE_NAMES.DASHBOARD_ACTIVITY_TASK, {
			activity_id: activity.activity_id,
			type: newTask.type
		}, {
			queryParams: currentQueryParams
		});
	}

	public onRecommendedDrop (event: CdkDragDrop<Task[]>): void {
		if (event.previousContainer !== event.container) {
			if (event.container.id === 'recommended-list' && event.previousContainer.id === 'task-list') {
				const task = event.item.data as Task;
				this.onTaskRemove(task);
			}
			else if (event.container.id === 'task-list' && event.previousContainer.id === 'recommended-list') {
				this.onAddRecommendedTask(event.item.data, event.currentIndex);
			}
		}
	}

	private processAndUpdateTasks (tasks: Task[], activity: Activity): void {
		const editorVisibleTasks = this.editorContext != null
			? tasks.filter((task) => task.type !== TaskType.Evaluate)
			: tasks;
		const deletedTasks = editorVisibleTasks.filter((task) => task.deleted_at);
		const activeTasks = editorVisibleTasks.filter((task) => !task.deleted_at);

		if (!this.hasUnsavedChanges()) {
			this.deletedTasks.clear();
			deletedTasks.forEach((task) => {
				this.deletedTasks.set(task.type, {
					...task,
					name: this.activityService.getTaskTitleAndDescription(activity, task).title
				});
			});
		}

		const filteredTasks = activeTasks
			.sort((a, b) => a.sort_order - b.sort_order)
			.map((task) => ({
				...task,
				name: this.activityService.getTaskTitleAndDescription(activity, task).title
			}));

		this.filteredTasks$.next(filteredTasks);
		this.updateLearnerTourState(filteredTasks);

		if (this.editorContext != null || !this.hasUnsavedChanges()) {
			this.modifiedTasks = [...filteredTasks];
			this.modifiedTasks$.next([...filteredTasks]);
		}
	}

	private getRecommendedTasks (): Observable<Task[]> {
		return combineLatest([
			this.filteredTasks$,
			this.modifiedTasks$,
			this.refreshRecommended$.pipe(startWith(null))
		]).pipe(
			map(([tasks, modifiedTasks]) => {
				const activity = this.getCurrentActivity();
				if (activity == null) {
					return [];
				}
				const tasksToCheck = this.isEditMode ? modifiedTasks : tasks;
				const existingTypes = new Set(tasksToCheck.map((task) => task.type));
				const recommendedTasks = this.taskService.getCompatibleTasks(activity)
					.filter((task) => !existingTypes.has(task.type));

				return recommendedTasks.map((task) => ({
					...task,
					name: this.activityService.getTaskTitleAndDescription(activity, task).title
				}));
			})
		);
	}

	private updateTaskSortOrders (): void {
		// Get the latest task data to preserve any edits made to individual tasks
		const tasks: Task[] = this.getCurrentTasks();

		this.modifiedTasks = this.modifiedTasks.map((task, index) => {
			const latestTask: Task = tasks.find((t) => this.taskMatches(t, task));
			const mergedTask = {
				...(latestTask || task),  // Use latest task data if available, fallback to current
				sort_order: index + 1
			};
			// This prevents restored tasks from getting their deleted_at value back
			if (task.deleted_at === null && latestTask?.deleted_at) {
				mergedTask.deleted_at = null;
				mergedTask.deleted_by = null;
			}
			return mergedTask;
		});
		this.modifiedTasks$.next(this.modifiedTasks);
	}

	public resetModifiedTasks (): void {
		this.modifiedTasks = [...this.filteredTasks$.value];
		this.modifiedTasks$.next(this.modifiedTasks);
		this.deletedTasks.clear();
		this.refreshRecommended$.next();
	}

	private updateLearnerTourState (tasks: Task[]): void {
		const incomplete = tasks.filter((task) => !task.completed);
		this.firstIncompleteTaskType = incomplete[0]?.type ?? null;
	}

	private initializeEditorContextMode (): void {
		this.isEditMode = this.editorContext.isEditMode();
		this.selectedTaskType = this.editorContext.selectedTaskType();
		this.recommendedTasks$ = this.getRecommendedTasks();

		effect(() => {
			const activity = this.editorContext.activity();
			this.isEditMode = this.editorContext.isEditMode();
			this.selectedTaskType = this.editorContext.selectedTaskType();

			if (activity != null) {
				this.processAndUpdateTasks(this.editorContext.tasks(), activity);
				if (!this.isKeyboardReorderActive) {
					this.updateOriginalIndex();
				}
			}
		}, { injector: this.injector });
	}

	private getCurrentActivity (): Activity | null {
		if (this.editorContext != null) {
			return this.editorContext.activity();
		}

		return this.selectedService.getActivity();
	}

	private getCurrentTasks (): Task[] {
		return this.editorContext?.tasks() ?? this.activityEditModeService.currentTasks();
	}

	private getVisibleTasksForFocus (): Task[] {
		const tasks = this.isEditMode ? this.modifiedTasks : this.filteredTasks$.value;
		const excludedTypes = this.excludedTypes$.value;
		return tasks.filter((task) => !excludedTypes.has(task.type));
	}

	private hasUnsavedChanges (): boolean {
		return this.editorContext?.isDirty() ?? this.activityEditModeService.hasUnsavedChanges();
	}

	private updateEditedTasks (tasks: Task[]): void {
		if (this.editorContext != null) {
			this.editorContext.updateTasks(tasks);
			return;
		}

		this.activityEditModeService.updateEditedTasks(tasks);
	}

	private loadDefaultActivityPrivacy (): void {
		this.activityService.getDefault()
			.pipe(takeUntilDestroyed(this.#destroyRef))
			.subscribe(({ activity }) => {
				this.defaultActivityPrivacy = {
					peer_review: activity.peer_review,
					public_feedback: activity.public_feedback
				};
			});
	}

	private applyDefaultPrivacyForReactTask (task: Task): void {
		if (task.type !== TaskType.React) {
			return;
		}

		const activity = this.getCurrentActivity();
		if (activity == null) {
			return;
		}

		const privacyDefault = this.defaultActivityPrivacy ?? activity;
		const publicFeedback = privacyDefault.peer_review === true && privacyDefault.public_feedback === true;
		const updatedActivity = {
			...activity,
			peer_review: true,
			public_feedback: publicFeedback
		} as Activity;

		if (this.editorContext != null) {
			this.editorContext.updateActivity(updatedActivity);
			return;
		}

		this.activityEditModeService.updateEditedActivity(updatedActivity);
	}

	private taskMatches (left: Task, right: Task): boolean {
		if (left.id !== 0 && right.id !== 0) {
			return left.id === right.id;
		}

		return left.type === right.type;
	}

	private isNestedTaskControl (target: EventTarget | null): boolean {
		if (!(target instanceof HTMLElement)) {
			return false;
		}

		return target.closest('.drag-handle, button, input, select, textarea, [role="button"]') != null;
	}
}
